// src/router/router.tsx
import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, Outlet, useLocation } from 'react-router-dom';
import { Home } from './pages';
import MyHeader from "./components/myheader/MyHeader";
import { CoursePage } from "./pages/course/CoursePage";
import InviteCodeVerification from './components/InviteCodeVerification';

import { COURSE_ROUTE, HOME_ROUTE, PROFILE_ROUTE, UPLOAD_ROUTE, ORDERS_ROUTE, PAYMENT_RETURN_ROUTE } from "./router/routePaths";
import { useAppSelector } from "./hooks";
import styles from './App.module.css';

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
    const isMember = useAppSelector((state) => state.user.isMember);

    if (!isMember) {
        return <Navigate to={HOME_ROUTE} replace />;
    }
    return element;
};

const AuthRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
    const isLogin = useAppSelector((state) => state.user.isLogin);

    if (!isLogin) {
        return <Navigate to={HOME_ROUTE} replace />;
    }
    return element;
};

const UserProfile = lazy(() => import('./pages/profile/UserProfile'));
const UploadPage = lazy(() => import('./pages/upload/UploadPage'));
const OrdersTable = lazy(() => import('./pages/orders/OrdersTable'));
const AlipayReturn = lazy(() => import('./pages/payment/AlipayReturn'));

const Layout = () => {
    const location = useLocation();
    const isCoursePage = location.pathname.startsWith('/course/');

    return (
        <div className={styles.container}>
            <MyHeader />
            <main className={styles.content}>
                <Outlet />
            </main>
            {!isCoursePage && (
                <footer className={styles.footer}>
                    <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">沪ICP备2025109127号-2</a> | jikeabc@aliyun.com | QQ:3863848634 | 举报邮箱：jikeabc@aliyun.com
                </footer>
            )}
        </div>
    );
};

const router = createBrowserRouter([
    {
        path: HOME_ROUTE,
        element: <Layout />,
        children: [
            {
                index: true,
                element: <Home />,
            },
            {
                path: COURSE_ROUTE,
                element: <CoursePage />,
                children: [
                    {
                        path: ":lessonId",
                        element: <CoursePage />,
                    }
                ]
            },
            {
                path: PROFILE_ROUTE,
                element: (
                    <Suspense fallback={<div>加载中...</div>}>
                        <AuthRoute element={<UserProfile />} />
                    </Suspense>
                ),
            },
            {
                path: UPLOAD_ROUTE,
                element: (
                    <Suspense fallback={<div>加载中...</div>}>
                        <ProtectedRoute element={<UploadPage />} />
                    </Suspense>
                ),
            },
            {
                path: ORDERS_ROUTE,
                element: (
                    <Suspense fallback={<div>加载中...</div>}>
                        <AuthRoute element={<OrdersTable />} />
                    </Suspense>
                ),
            },
            {
                path: PAYMENT_RETURN_ROUTE,
                element: (
                    <Suspense fallback={<div>加载中...</div>}>
                        <AlipayReturn />
                    </Suspense>
                ),
            }
        ]
    }
]);

const App: React.FC = () => {
    return <RouterProvider router={router} />;
}

export default App;
